import {
  Typography,
  Container,
  Grid,
  styled,
  useTheme,
  useMediaQuery,
  Box,
  Button,
  Paper,
  TextField,
  FormControl,
  MenuItem,
} from '@mui/material';
import CustomAlert from '../components/CustomAlert';
import Page from '../components/Page';
import * as yup from 'yup';
import { Formik } from 'formik';
import { store } from '../store';
import { taxTypes } from '../utils/hardcoded';
import { useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  cleanOfflineImponibleState,
  errorOfflineImponible,
  searchImponible,
  offlineImponible,
  statusOfflineImponible,
} from '../features/offlineImponible/offlineImponibleSlice';
import { LoadingButton } from '@mui/lab';
import { getLabelClaveComprobante } from '../utils';

const FormWrapper = styled(Paper)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '1.5rem 2rem 2rem 2rem',
  justifyContent: 'space-between',
  border: '2px solid',
  borderColor: theme.palette.primary.main,
  marginLeft: '2rem',
  [theme.breakpoints.down('lg')]: {
    marginLeft: 0,
    marginTop: '2rem',
    marginBottom: '1rem',
  },
}));

const MainWrapper = styled(Paper)(({ theme }) => ({
  border: '1px solid',
  borderColor: theme.palette.grey[300],
  display: 'flex',
  flexDirection: 'column',
  padding: '2rem',
}));

export default function PayBill() {
  const theme = useTheme();
  const navigate = useNavigate();

  const desktop = useMediaQuery(theme.breakpoints.up('md'));
  const imponible = useSelector(offlineImponible);
  // const status = useSelector(statusOfflineImponible);
  // const error = useSelector(errorOfflineImponible);

  const statusFromStore = useSelector(statusOfflineImponible);
  const errorFromStore = useSelector(errorOfflineImponible);

  const [status, setStatus] = useState(statusFromStore || 'idle');
  const [error, setError] = useState(errorFromStore || '');
  const paymentValidationSchema = yup.object().shape({
    tipo: yup
      .string()
      .test(
        'is-required',
        'Imponible es requerido',
        value => value !== undefined
      ),
    id: yup
      .string()
      .when('tipo', {
        is: 'INM',
        then: yup.string().required('El campo partida es obligatorio'),
      })
      .when('tipo', {
        is: 'CEM',
        then: yup.string().required('El campo número de nicho es obligatorio'),
      })
      .when('tipo', {
        is: 'ROD',
        then: yup.string().required('El campo dominio es obligatorio'),
      })
      .when('tipo', {
        is: 'COM',
        then: yup.string().required('El campo número de comercio es obligatorio'),
      })
      .when('tipo', {
        is: 'CON',
        then: yup
          .string()
          .length(11, 'El Cuit debe ser de 11 caracteres')
          .required('El campo CUIT/CUIL es obligatorio'),
      })
      .when('tipo', {
        is: 'ALM',
        then: yup
          .string()
          .length(11, 'El Cuit debe ser de 11 caracteres')
          .required('El campo CUIT/CUIL es obligatorio'),
      }),
  });

  useEffect(() => {
    setStatus(statusFromStore || 'idle');
    setError(errorFromStore || '');
  }, [statusFromStore, errorFromStore]);

  useEffect(() => {
    if (imponible) {
      if (status === 'success') {
        if (imponible.recursos.length > 0) {
          navigate('/paga-tu-tasa/consulta');
        } else {
          setStatus('error');
          setError('No se encontraron tasas a pagar con el CUIT ingresado.');
        }
      }
    }
  }, [imponible, status]);

  return (
    <Page>
      <Container>
        <Grid
          container
          padding={'1.875rem'}
          justifyContent="center"
          direction={desktop ? 'row' : 'column'}
        >
          <Grid item sm={10} sx={{ alignSelf: 'center' }}>
            <Box mb={3}>
              <MainWrapper>
                <Grid container direction="row">
                  <Grid item md={12} lg={7}>
                    <Typography variant="subtitle1" mb="1rem">
                      Pagá tu tasa y contribuciones
                    </Typography>
                    <Typography variant="body1" mb=".5rem">
                      Esta sección permite pagar tu factura de tasas de
                      Inmuebles, Rodados, Comercios, Contribuyentes y Contribuciones.
                    </Typography>
                    <Typography variant="body1" mb="1rem">
                      Deberás completar el tipo de imponible y el número.
                    </Typography>
                    <Typography variant="body1">
                      - Para tasas a los Inmuebles: Número de partida.
                    </Typography>
                    <Typography variant="body1">
                      - Para tasas a los Contribuyentes: Número de CUIT o CUIL.
                    </Typography>
                    <Typography variant="body1">
                      - Para tasas a los Comercios: Número de comercio.
                    </Typography>
                    <Typography variant="body1">
                      - Para tasas a los Rodados: Número de dominio (patente).
                    </Typography>
                    <Typography variant="body1">
                      - Para alumnos: Número de CUIT o CUIL.
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={12} lg={5}>
                    <Box>
                      <Formik
                        initialValues={{
                          tipo: '',
                          id: '',
                        }}
                        validationSchema={paymentValidationSchema}
                        onSubmit={values => {
                          const payload = {
                            tipo: values.tipo,
                            buscar: values.id,
                          };
                          store.dispatch(searchImponible(payload));
                        }}
                      >
                        {({
                          handleSubmit,
                          setFieldValue,
                          values,
                          errors,
                          touched,
                          resetForm,
                        }) => (
                          <FormWrapper>
                            <Typography variant="h6" component="h2" mb="1rem">
                              Completá tus datos
                            </Typography>

                            <FormControl fullWidth>
                              <TextField
                                select
                                id="taxable"
                                label="Imponible"
                                value={values.tipo}
                                onChange={event => {
                                  setFieldValue('tipo', event.target.value);
                                }}
                                sx={{ marginBottom: '1rem' }}
                                error={touched.tipo && Boolean(errors.tipo)}
                                helperText={touched.tipo && errors.tipo}
                              >
                                {taxTypes.map((tax, i) => (
                                  <MenuItem key={i} value={tax.value}>
                                    {tax.name}
                                  </MenuItem>
                                ))}
                              </TextField>

                              {values.tipo !== '' ? (
                                <TextField
                                  variant="outlined"
                                  id="taxabletype"
                                  value={values.id}
                                  onChange={e => {
                                    setFieldValue('id', e.target.value.toLocaleUpperCase());
                                  }}
                                  sx={{ marginBottom: '1rem' }}
                                  error={touched.id && Boolean(errors.id)}
                                  helperText={touched.id && errors.id}
                                  label={getLabelClaveComprobante(values.tipo)}
                                />
                              ) : null}
                            </FormControl>

                            <CustomAlert status={status} errorMessage={error} style={{ marginBottom: '1rem' }} />

                            <Box sx={{ display: 'flex', width: '100%' }}>
                              <LoadingButton
                                loading={
                                  status === 'idle' || status === 'error'
                                    ? false
                                    : true
                                }
                                variant="contained"
                                onClick={() => handleSubmit()}
                                sx={{
                                  width: '100%',
                                  marginRight: '1rem',
                                }}
                              >
                                Enviar
                              </LoadingButton>
                              <Button
                                variant="outlined"
                                sx={{ width: '100%' }}
                                onClick={() => {
                                  resetForm();
                                  store.dispatch(cleanOfflineImponibleState());
                                }}
                              >
                                Cancelar
                              </Button>
                            </Box>
                          </FormWrapper>
                        )}
                      </Formik>
                    </Box>
                  </Grid>
                </Grid>
              </MainWrapper>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
}